// @ts-ignore
import type * as RCL from 'rclnodejs';

import type { FrameOfReference } from '@sb/motion-planning';
import { getRCL } from '@sb/ros/getRCL';

import { BaseService } from './BaseService';
import { JOINT_NAMES } from './constants';

export interface DirectionValidation {
  x: boolean;
  y: boolean;
  z: boolean;
  roll: boolean;
  pitch: boolean;
  yaw: boolean;
}

export interface FrameDirections {
  positive: DirectionValidation;
  negative: DirectionValidation;
}

export class GetToolDirectionsService extends BaseService<
  'standard_bots_msgs/srv/GetToolDirections',
  RCL.standard_bots_msgs.srv.GetToolDirections_Request,
  RCL.standard_bots_msgs.srv.GetToolDirections_Response
> {
  private jointPositions: number[];

  private translationDistance: number;

  private rotationAngle: number;

  private frameOfReference: FrameOfReference;

  public constructor(
    jointPositions: number[],
    translationDistance: number,
    rotationAngle: number,
    frameOfReference: FrameOfReference,
  ) {
    super();
    this.jointPositions = jointPositions;
    this.translationDistance = translationDistance;
    this.rotationAngle = rotationAngle;
    this.frameOfReference = frameOfReference;
  }

  protected override getType(): RCL.TypeClass<keyof RCL.ServicesMap> {
    return 'standard_bots_msgs/srv/GetToolDirections';
  }

  protected override getServiceName(): string {
    return 'get_tool_directions';
  }

  protected override async createRequest() {
    const rcl = await getRCL();

    const GetToolDirections = rcl.require(
      'standard_bots_msgs/srv/GetToolDirections',
    );

    const request = new GetToolDirections.Request();

    const jointState = rcl.createMessageObject('sensor_msgs/msg/JointState');

    jointState.name = [...JOINT_NAMES];
    jointState.position = this.jointPositions;
    request.joint_state = jointState;
    request.translation_distance = this.translationDistance;
    request.rotation_angle = this.rotationAngle;
    request.frame_of_reference = this.frameOfReference;

    return request;
  }

  protected override getTimeout(): number {
    return 1000; // 1 second timeout (validates 12 directions)
  }

  public async getResult(): Promise<FrameDirections> {
    const response = await this.call();

    return {
      positive: {
        x: response.positive_x,
        y: response.positive_y,
        z: response.positive_z,
        roll: response.positive_roll,
        pitch: response.positive_pitch,
        yaw: response.positive_yaw,
      },
      negative: {
        x: response.negative_x,
        y: response.negative_y,
        z: response.negative_z,
        roll: response.negative_roll,
        pitch: response.negative_pitch,
        yaw: response.negative_yaw,
      },
    };
  }
}
